import { useAsync } from 'react-use';

import { GroupEntity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { isUserManagerOfTeam } from './useManagedTeamWithChildren';

export interface TeamDescendantsTree {
  team_id: string;
  team_ou: string;
  team_bu: string;
  children: TeamDescendantsTree[];
}

export interface TeamTreeResponse {
  tree: TeamDescendantsTree;
  flatTree: string[];
}

export interface MemberTeam {
  name: string;
  title: string;
  description: string;
  ou?: string;
  bu?: string;
}

export interface TeamHierarchyFromAPI {
  managedTrees: TeamDescendantsTree[];
  memberTeams: MemberTeam[];
}

/**
 * Custom hook that fetches team hierarchy from the team-tree API.
 *
 * Logic:
 * 1. Gets user's email from identity API
 * 2. Gets all teams user is a member of from catalog
 * 3. Filters to teams where user is manager (in metadata.managers)
 * 4. Calls /api/common-utilities/team-tree/:teamName for EACH managed team
 * 5. Extracts member-only teams (not in any managed tree)
 *
 * @returns {TeamHierarchyFromAPI} - Managed trees and member teams
 */
export const useTeamTreeFromAPI = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const {
    value: hierarchy,
    loading,
    error,
  } = useAsync(async (): Promise<TeamHierarchyFromAPI> => {
    try {
      // Step 1: Get user email
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const { token } = await identityApi.getCredentials();
      const userEntity = await catalogApi.getEntityByRef(userEntityRef);

      const profile = userEntity?.spec?.profile as
        | { email?: string }
        | undefined;
      const userEmail = profile?.email;

      if (!userEmail) {
        return { managedTrees: [], memberTeams: [] };
      }

      // Step 2: Get all teams user is a member of
      const groupRefs =
        userEntity?.relations
          ?.filter(
            r => r.type === 'memberOf' && r.targetRef.startsWith('group:'),
          )
          .map(r => r.targetRef) || [];

      if (groupRefs.length === 0) {
        return { managedTrees: [], memberTeams: [] };
      }

      // Step 3: Fetch all group entities (in parallel)
      const groupEntities = (
        await Promise.all(
          groupRefs.map(async ref => {
            try {
              return await catalogApi.getEntityByRef(ref);
            } catch {
              return null;
            }
          }),
        )
      ).filter((group): group is GroupEntity => group !== null);

      // Step 4: Filter to teams where user is manager
      const managedTeams = groupEntities.filter(group =>
        isUserManagerOfTeam(group, userEmail),
      );

      if (managedTeams.length === 0) {
        // User is not a manager, fetch OU/BU for all member teams
        const baseUrl = await discoveryApi.getBaseUrl('common-utilities');

        const memberTeamsWithOuBu = await Promise.all(
          groupEntities.map(async group => {
            try {
              const response = await fetch(
                `${baseUrl}/team-tree/${group.metadata.name}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                },
              );

              if (response.ok) {
                const data = (await response.json()) as TeamTreeResponse;
                return {
                  name: group.metadata.name,
                  title: group.metadata.title || group.metadata.name,
                  description: group.metadata.description || 'N/A',
                  ou:
                    data.tree.team_ou && data.tree.team_ou.trim() !== ''
                      ? data.tree.team_ou
                      : undefined,
                  bu:
                    data.tree.team_bu && data.tree.team_bu.trim() !== ''
                      ? data.tree.team_bu
                      : undefined,
                };
              }
            } catch {
              // Ignore errors, return without OU/BU
            }

            return {
              name: group.metadata.name,
              title: group.metadata.title || group.metadata.name,
              description: group.metadata.description || 'N/A',
            };
          }),
        );

        return {
          managedTrees: [],
          memberTeams: memberTeamsWithOuBu,
        };
      }

      // Step 5: Find top-level managed teams (teams whose parents aren't also managed)
      const managedTeamRefs = new Set(
        managedTeams.map(t => `group:default/${t.metadata.name}`),
      );

      const topLevelManagedTeams = managedTeams.filter(team => {
        const parentRef = team.spec?.parent as string | undefined;

        if (!parentRef) {
          return true; // No parent = top-level
        }

        // Normalize parent ref
        let normalizedParent: string;
        if (parentRef.startsWith('group:default/')) {
          normalizedParent = parentRef;
        } else if (parentRef.startsWith('group:')) {
          normalizedParent = parentRef.replace('group:', 'group:default/');
        } else {
          normalizedParent = `group:default/${parentRef}`;
        }

        return !managedTeamRefs.has(normalizedParent);
      });

      // Step 6: Call team-tree API only for top-level managed teams
      const baseUrl = await discoveryApi.getBaseUrl('common-utilities');

      const treeResults = await Promise.all(
        topLevelManagedTeams.map(async team => {
          try {
            const response = await fetch(
              `${baseUrl}/team-tree/${team.metadata.name}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              },
            );

            if (!response.ok) {
              throw new Error(`Failed to fetch tree for ${team.metadata.name}`);
            }

            return (await response.json()) as TeamTreeResponse;
          } catch (fetchError) {
            // Return null for failed fetches, we'll filter them out
            return null;
          }
        }),
      );

      // Filter out failed fetches
      const validTrees = treeResults.filter(
        (result): result is TeamTreeResponse => result !== null,
      );

      // Step 7: Extract all team IDs from managed trees
      const allManagedTeamIds = new Set(
        validTrees.flatMap(result => result.flatTree),
      );

      // Step 8: Filter member-only teams (not in any managed tree)
      const memberOnlyGroups = groupEntities.filter(
        group =>
          !allManagedTeamIds.has(group.metadata.name) &&
          !managedTeams.some(mt => mt.metadata.name === group.metadata.name),
      );

      // Step 9: Fetch OU/BU info for member-only teams from team-tree API
      const memberTeamsWithOuBu = await Promise.all(
        memberOnlyGroups.map(async group => {
          try {
            const response = await fetch(
              `${baseUrl}/team-tree/${group.metadata.name}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              },
            );

            if (response.ok) {
              const data = (await response.json()) as TeamTreeResponse;
              return {
                name: group.metadata.name,
                title: group.metadata.title || group.metadata.name,
                description: group.metadata.description || 'N/A',
                ou:
                  data.tree.team_ou && data.tree.team_ou.trim() !== ''
                    ? data.tree.team_ou
                    : undefined,
                bu:
                  data.tree.team_bu && data.tree.team_bu.trim() !== ''
                    ? data.tree.team_bu
                    : undefined,
              };
            }
          } catch {
            // Ignore errors, return without OU/BU
          }

          return {
            name: group.metadata.name,
            title: group.metadata.title || group.metadata.name,
            description: group.metadata.description || 'N/A',
          };
        }),
      );

      return {
        managedTrees: validTrees.map(result => result.tree),
        memberTeams: memberTeamsWithOuBu,
      };
    } catch (err) {
      throw new Error(
        `Failed to fetch team hierarchy: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    }
  }, [identityApi, catalogApi, discoveryApi]);

  return {
    managedTrees: hierarchy?.managedTrees || [],
    memberTeams: hierarchy?.memberTeams || [],
    loading,
    error,
  };
};
